// 🚀 Orval 자동 생성 훅과 함수 사용 - useApi와 useInfiniteData 제거!
import { useEffect, useMemo, useRef } from 'react';
import {
  getCourseDrafts,
  publishCourseDraft,
  CourseDraftSummary,
  BaseUserInfo,
  Price,
  GetCourseDraftsParams,
  GetCourseDraftsFilter as CourseDraftFilterEnum,
  GetApiCoursesType,
} from '@edusense/course-client';
import { useAuth } from '@edusense/shared-auth-core';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

// 초안 강의 목록을 위한 타입 정의
// Course 타입과 유사하게 만들어 CourseList와의 호환성을 높입니다.
export interface TransformedCourseDraft {
  id: string;
  title?: string | null;
  instructor?: BaseUserInfo | null; // BaseUserInfo 타입 사용
  description?: string | null;
  tags?: string[] | null;
  level?: string | null;
  enrollmentCount?: number | null;
  averageRating?: string | null;
  reviewCount?: string | null;
  price?: Price | null; // Price 타입 사용
  isFree?: boolean;
  isCompleted?: boolean;
  owned?: boolean;
  progressRate?: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  thumbnailUrl?: string | null;
  // 필요한 경우 초안 관련 추가 상태 (예: 'draft', 'pending_review')
  statusLabel?: string;
  isPublished: boolean;
}

type CourseDraftFilter = typeof CourseDraftFilterEnum[keyof typeof CourseDraftFilterEnum];

interface UseCourseDraftDataOptions {
  filter?: CourseDraftFilter;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * 🚀 완전 자동화된 강의 초안 데이터 훅 - Orval + React Query
 * 기존의 복잡한 상태관리와 API 호출 로직을 완전히 제거하고 
 * 자동 생성된 React Query 훅을 사용합니다.
 */
export function useCourseDraftData({
  filter = CourseDraftFilterEnum.my_drafts,
  pageSize = 10,
  enabled = true,
}: UseCourseDraftDataOptions = {}) {
  const { isAuthenticated } = useAuth();
  const observerRef = useRef<HTMLDivElement>(null);

  const queryResult = useInfiniteQuery({
    queryKey: ['courseDrafts', filter || 'none', pageSize],
    enabled: enabled && isAuthenticated,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async ({ pageParam = 0 }) => {
      const params: GetCourseDraftsParams = {
        page: pageParam,
        size: pageSize,
        ...(filter ? { filter } : {}),
      };

      console.log('[useCourseDraftData] 요청 파라미터:', params);

      const responseData = await getCourseDrafts(params);
      const draftList: CourseDraftSummary[] = responseData?.content || [];

      const transformedDrafts = draftList.map(
        (item): TransformedCourseDraft => {
          console.log('[useCourseDraftData] Draft item:', item);
          console.log('[useCourseDraftData] instructor:', item.instructor);

          return {
            id: String(item.id),
            title: item.title || '제목 없는 초안',
            instructor: item.instructor || null,
            description: item.description || null,
            tags: item.tags || null,
            level: item.level || null,
            enrollmentCount: item.enrollmentCount || 0,
            averageRating: item.averageRating || null,
            reviewCount: item.reviewCount || null,
            price: item.price || null,
            isFree: !item.price || item.price.amount === 0,
            isCompleted: false,
            owned: true,
            progressRate: 0,
            createdAt: item.createdAt || null,
            updatedAt: item.updatedAt,
            thumbnailUrl: item.thumbnailUrl ?? '/images/placeholder-course-thumbnail.png',
            statusLabel: '초안',
            isPublished: item.isPublished ?? false,
          };
        },
      );

      return {
        drafts: transformedDrafts,
        pagination: {
          last: responseData?.last ?? true,
          number: responseData?.number ?? pageParam,
        },
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.last) {
        return undefined;
      }
      return (lastPage.pagination.number ?? 0) + 1;
    },
  });

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = queryResult;

  const transformedData = useMemo(
    () => data?.pages?.flatMap((page) => page.drafts) ?? [],
    [data],
  );

  useEffect(() => {
    const target = observerRef.current;
    if (!target) {
      return;
    }

    if (!(enabled && isAuthenticated)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }

        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px 200px 0px',
      },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [enabled, isAuthenticated, fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    data: transformedData,
    isLoading,
    error: error as Error | null,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    observerRef,
    refresh: refetch,
    pagination: {
      last: !(hasNextPage ?? false),
      totalPages: data?.pages?.length ?? 0,
      totalElements: transformedData.length,
    },
  };
}

/**
 * 🚀 강의 초안 발행을 위한 커스텀 훅 - Orval 자동 생성 함수 사용
 */
export function usePublishCourseDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draftId: string) => {
      // 🚀 자동 생성된 함수로 직접 호출 - 간소화!
      const response = await publishCourseDraft({ id: draftId });
      return response.data;
    },
    onSuccess: (data, draftId) => {
      console.log(`Draft ${draftId} published successfully:`, data);
      queryClient.invalidateQueries({ queryKey: ['courseDrafts', 'my_drafts'] });

      // "내가 게시한 강의" 목록 쿼리도 무효화
      queryClient.invalidateQueries({ queryKey: ['courses', GetApiCoursesType.MY_PUBLISHED] });
    },
    onError: (error, draftId) => {
      console.error(`Failed to publish draft ${draftId}:`, error);
    },
  });
} 
